import React, { useState, useCallback, createContext, useContext, type ReactNode } from 'react';
import { translations } from '../translations';
import type { LanguageContextType } from '../translations';
import { db } from '../firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';

type Lang = 'th' | 'en';

// setCurrentUserId / setUserPreferredLanguage are called by AuthContext to sync
// the preference stored on the user profile.
type LanguageContextValue = LanguageContextType & {
  setCurrentUserId: (userId: string | null) => void;
  setUserPreferredLanguage: (lang: Lang) => void;
};

export const LanguageContext = createContext<LanguageContextValue | null>(null);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Lang>('th');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Set language and persist it on the user profile when logged in
  const setLanguage = useCallback(async (newLanguage: Lang) => {
    setLanguageState(newLanguage);
    if (currentUserId) {
      try {
        await updateDoc(doc(db, 'users', currentUserId), { preferredLanguage: newLanguage });
      } catch {
        // Preference persistence is best-effort
      }
    }
  }, [currentUserId]);

  const t = useCallback<LanguageContextType['t']>((key, options) => {
    // Fall back to English, then defaultValue, then the key itself
    let message = translations[language]?.[key] ?? translations.en?.[key];
    if (message === undefined) return options?.defaultValue ?? key;
    if (options) {
      for (const [interpKey, value] of Object.entries(options)) {
        if (interpKey !== 'defaultValue' && value !== undefined) {
          message = message.replace(new RegExp(`{{${interpKey}}}`, 'g'), String(value));
        }
      }
    }
    return message;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, setCurrentUserId, setUserPreferredLanguage: setLanguageState }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
